import type { JobStatus } from '../models/entity/job.entity';
import type { DefaultStatusValueModel } from './competitor-engagement-statuses';

/**
 * PD-17448 — the customers on a job, each with a role and a status (owner
 * decision 1, 10/24/26): the contractor buying the material, the general
 * contractor, the building owner, a contractor still bidding. Both are
 * tenant-editable catalog lists (catalog_value, types `job_customer_role` and
 * `job_customer_status`), seeded with the values below. They replace the
 * project participants of the removed Projects feature.
 *
 * A role has no fixed meaning: it is a label. Every status maps to one of
 * the fixed OUTCOMES below, the way a competitor status does, so code (the
 * badge color, the status a new job's customer starts with) keys off the
 * outcome and never off a name a tenant may change.
 */

/** Seeded per tenant; admins rename, reorder or add in Settings → Catalog Values. */
export const DEFAULT_JOB_CUSTOMER_ROLES: ReadonlyArray<string> = [
	'Contractor',
	'General contractor',
	'Owner',
	'Bidding contractor',
	'Other',
];

/**
 * Where a customer stands on a job, whatever the tenant calls it:
 * - bidding: pricing the work, not decided;
 * - awarded: has the work (buys the material for it);
 * - lost: did not get the work;
 * - not_buying: on the job, but buys nothing from the tenant for it.
 */
export const JOB_CUSTOMER_STATUS_OUTCOMES = ['bidding', 'awarded', 'lost', 'not_buying'] as const;
export type JobCustomerStatusOutcome = (typeof JOB_CUSTOMER_STATUS_OUTCOMES)[number];

export const JOB_CUSTOMER_STATUS_OUTCOME_LABELS: Readonly<Record<JobCustomerStatusOutcome, string>> = {
	bidding: 'Bidding',
	awarded: 'Awarded',
	lost: 'Lost',
	not_buying: 'Not buying',
};

/** Seeded per tenant, each value with the outcome it means. */
export const DEFAULT_JOB_CUSTOMER_STATUSES: ReadonlyArray<DefaultStatusValueModel> = [
	{ name: 'Bidding', outcome: 'bidding' },
	{ name: 'Awarded', outcome: 'awarded' },
	{ name: 'Lost', outcome: 'lost' },
	{ name: 'Not buying', outcome: 'not_buying' },
];

/**
 * The status a job's first customer starts with, from the job's own status:
 * a planned job is still being bid; an active, on-hold or closed one was
 * awarded to the customer it was created for. Used when a job is created and
 * by the migration that gives every existing job its customer.
 */
export function jobCustomerStatusOutcomeForJob(status: JobStatus | string | null | undefined): JobCustomerStatusOutcome {
	return status === 'planned' ? 'bidding' : 'awarded';
}
